import React from 'react';
import { Delete, Edit } from '@mui/icons-material';
import {
	SchedulerDepartment,
	deleteDepartment,
	JobCode,
} from '../../../../core/API/scheduler';
import { isJobCodeReferencedByDepartment } from '../../../../module/schedule/utils/scheduleDepartmentUtils';
import styles from '../SchedulerSetting.module.scss';

interface DepartmentsListProps {
	departments: SchedulerDepartment[];
	jobCodes: JobCode[];
	onDepartmentChanged: () => void;
	onEditClick: (department: SchedulerDepartment) => void;
	canEdit?: boolean;
	canDelete?: boolean;
}

const DepartmentsList: React.FC<DepartmentsListProps> = ({
	departments,
	jobCodes,
	onDepartmentChanged,
	onEditClick,
	canEdit = true,
	canDelete = true,
}) => {
	const handleDeleteClick = async (department: SchedulerDepartment) => {
		if (!canDelete) return;

		if (isJobCodeReferencedByDepartment(jobCodes, department.id)) {
			window.alert('Cannot delete department while it is assigned to a job code.');
			return;
		}

		const ok = window.confirm(
			`Are you sure you want to delete department "${department.name}"? This action cannot be undone.`
		);
		if (!ok) return;

		try {
			await deleteDepartment(department.id);
			onDepartmentChanged();
		} catch (e: any) {
			const message =
				e?.response?.data?.['hydra:description'] ||
				e?.response?.data?.message ||
				e?.message ||
				'Failed to delete department';
			window.alert(message);
		}
	};

	const sortedDepartments = [...departments].sort((a, b) => a.name.localeCompare(b.name));

	return (
		<div className={styles.tableContainer}>
			<table className={styles.workplaceTable}>
				<thead>
					<tr>
						<th>Name</th>
						<th style={{ width: 48, textAlign: 'center' }}>Edit</th>
						<th style={{ width: 48, textAlign: 'center' }}>Delete</th>
					</tr>
				</thead>
				<tbody>
					{sortedDepartments.length === 0 ? (
						<tr>
							<td colSpan={3}>
								<span>No departments configured</span>
							</td>
						</tr>
					) : (
						sortedDepartments.map((department) => (
							<tr key={department.id}>
								<td>
									<span>{department.name}</span>
								</td>
								<td style={{ textAlign: 'center', padding: '8px', width: 48 }}>
									<button
										className={styles.iconButton}
										title={canEdit ? 'Edit' : 'You do not have permission to edit'}
										onClick={() => onEditClick(department)}
										disabled={!canEdit}
										style={{
											opacity: canEdit ? 1 : 0.5,
											cursor: canEdit ? 'pointer' : 'not-allowed',
										}}
									>
										<Edit className={styles.editIcon} />
									</button>
								</td>
								<td style={{ textAlign: 'center', padding: '8px', width: 48 }}>
									<button
										onClick={() => handleDeleteClick(department)}
										title={canDelete ? 'Delete Department' : 'You do not have permission to delete'}
										className={styles.iconButton}
										disabled={!canDelete}
										style={{
											opacity: canDelete ? 1 : 0.5,
											cursor: canDelete ? 'pointer' : 'not-allowed',
										}}
									>
										<Delete className={styles.deleteIcon} />
									</button>
								</td>
							</tr>
						))
					)}
				</tbody>
			</table>
		</div>
	);
};

export default DepartmentsList;
